import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Sidebar from "../Sidebar";
import { api1 } from "../../utils/axiosInstance";
import { getAILiteracyQuiz } from "../../services/dashboardService";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { setQuizCompleted, setTotalScore, setProficiencyLevel } from "../../redux/userSlice";
import { HEADING_COLOR } from "../../constants/colors";

const AILiteracyAssessmentQuiz = ({ onQuizComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isAnswered, setIsAnswered] = useState(false);
  const [dynamicQuizData, setDynamicQuizData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.id);
  const { targetJob, targetJobDescription } = useSelector((state) => state.user);
  const sectionRef = useRef(null);

  const quizData = dynamicQuizData || [];
  const currentQuestion = quizData[currentQuestionIndex];

  const totalQuestions = quizData.length;
  const currentQuestionNumber = currentQuestionIndex + 1;
  const progressPercentage = Math.round((Object.keys(selectedAnswers).length / totalQuestions) * 100);

  useEffect(() => {
    if (!targetJob) {
      setError('No target job found. Please select a target job to take the assessment.');
      setIsLoading(false);
      return;
    }

    const loadDynamicQuiz = async () => {
      try {
        setIsLoading(true);
        const response = await getAILiteracyQuiz(targetJob, targetJobDescription);

        if (response?.success && response?.data) {
          const transformedData = response.data.map((q) => ({
            question: q.question,
            options: q.options,
            correct_index: q.correct_index,
            rationale: q.rationale || ''
          }));
          setDynamicQuizData(transformedData);
        } else {
          setError('No quiz data received from server');
        }
      } catch (error) {
        console.error('Error loading dynamic quiz:', error);
        setError('Failed to load quiz data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDynamicQuiz();
  }, [targetJob, targetJobDescription]);

  useEffect(() => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setIsAnswered(selectedAnswers[currentQuestionIndex] !== undefined);
  }, [currentQuestionIndex, selectedAnswers]);

  const handleNavigation = (direction) => {
    if (direction === 'next' && currentQuestionIndex < quizData.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (direction === 'back' && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isAnswered) return;
    
    const correctAnswers = Object.entries(selectedAnswers).reduce((count, [qIdx, optIdx]) => {
      return count + (optIdx === quizData[qIdx].correct_index ? 1 : 0);
    }, 0);
    
    const totalScore = correctAnswers * 5;
    const proficiencyLevel = correctAnswers <= 3 ? "Beginner" : 
                            correctAnswers <= 6 ? "Emerging" : 
                            correctAnswers <= 9 ? "Proficient" : 
                            correctAnswers <= 12 ? "Advanced" : "Unclassified";

    const formattedResponses = Object.entries(selectedAnswers).map(([qIdx, optIdx]) => ({
      question: quizData[qIdx].question,
      selectedOption: String.fromCharCode(65 + optIdx),
      selectedIndex: optIdx,
      correctIndex: quizData[qIdx].correct_index,
      isCorrect: optIdx === quizData[qIdx].correct_index,
      rationale: quizData[qIdx].rationale || ''
    }));

    try {
      await api1.post("/api/quiz/submit", { userId, responses: formattedResponses });
      dispatch(setQuizCompleted(true));
      dispatch(setTotalScore(totalScore));
      dispatch(setProficiencyLevel(proficiencyLevel));
      
      onQuizComplete();
    } catch (error) {
      console.error("Error submitting assessment:", error);
    }
  };

  if (isLoading) {
    return (
      <Sidebar>
        <div className="py-2 max-w-5xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your personalized AI Literacy Assessment for {targetJob}...</p>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="py-2 max-w-5xl mx-auto text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </Sidebar>
    );
  }

  if (!currentQuestion) return null;

  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === quizData.length - 1;

  return (
    <Sidebar>
      <div className="py-2 max-w-5xl mx-auto">
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-center mb-4" style={{ color: HEADING_COLOR }}>AI Literacy Assessment</h1>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-700">Question {currentQuestionNumber} of {totalQuestions}</span>
            <span className="text-sm text-gray-700">{progressPercentage}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-8">
            <div className="bg-purple-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progressPercentage}%` }} />
          </div>
        </div>

        <div className="border border-gray-300 rounded-2xl shadow-lg p-6 mb-3" ref={sectionRef}>
          <span className="inline-block bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full mb-5">
            Question {currentQuestionNumber} of {totalQuestions}
          </span>

          <h2 className="text-lg font-bold text-gray-900 mb-4">{currentQuestion.question}</h2>

          <div className="space-y-4 mb-8">
            {currentQuestion.options.map((option, optionIndex) => {
              const isSelected = selectedAnswers[currentQuestionIndex] === optionIndex;
              return (
                <label
                  key={optionIndex}
                  className={`flex text-base items-center p-3 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 ${
                    isSelected ? 'border border-purple-500 bg-purple-50' : 'border border-gray-100 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestionIndex}`}
                    value={optionIndex}
                    checked={isSelected}
                    onChange={() => setSelectedAnswers(prev => ({ ...prev, [currentQuestionIndex]: optionIndex }))}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${isSelected ? 'border-purple-500' : 'border-gray-300'}`}>
                    {isSelected && <div className="w-3 h-3 bg-purple-500 rounded-full" />}
                  </div>
                  <span className="text-gray-800 font-medium">{option}</span>
                </label>
              );
            })}
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={() => handleNavigation('back')}
              disabled={isFirstQuestion}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isFirstQuestion ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Previous Question
            </button>

            {isLastQuestion ? (
              <button
                onClick={handleSubmit}
                disabled={!isAnswered}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  isAnswered ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Submit Assessment
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => handleNavigation('next')}
                disabled={!isAnswered}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-all duration-200 ${
                  isAnswered ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Next Question
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AILiteracyAssessmentQuiz;
